// src/components/proposals/webViewBadges.js
//
// "Opened" and "Signed" as DERIVED display badges, never stored, and never
// values in `proposal.status`.
//
// ⚠️ WHY THESE ARE NOT STATUSES. ProposalsTab renders the chip through
// `STATUS_STYLE[p.status]`, so an unrecognised value resolves to `undefined`
// and the chip renders blank — and the record also drops out of the dashboard's
// status filter, which matches on exact equality. Adding "opened" would
// therefore hide the very proposals it was meant to highlight.
//
// The line is between WORKFLOW STATE and ACTIVITY. proposalStatus.js owns the
// former, and it legitimately grew when internal approval shipped
// (waiting_approval / approved / changes_needed). Opened and Signed are the
// latter: metadata layered over whatever state the proposal is already in, so
// they stay here as derived badges.
//
// Kept React-free, like opportunityPipeline.js and docTypes.js, so the rules can
// be unit-tested without rendering anything.
import { contractsForProposal } from "../contracts/contractModel";

/* What is knowable WITHOUT a network call decides where each badge can appear.

   `publicUrl` and the contract instances both live in the state blob and are
   already in memory for every proposal, so Sent and Signed can render on the
   dashboard. Opens live in the per-proposal view blob and are fetched one
   proposal at a time — showing an open count on the dashboard would mean one
   request per card. So Opened appears only on the proposal that is open. */

export const BADGE_STYLE = {
  sent:   { bg: "#e0f2fe", fg: "#0369a1", label: "Link sent" },
  opened: { bg: "#dcfce7", fg: "#15803d", label: "Opened" },
  asked:  { bg: "#fef3c7", fg: "#b45309", label: "Question" },
  signed: { bg: "#eefaef", fg: "#1e7d34", label: "Signed" },
};

/** Has this proposal been published to a customer link at all? */
export function isPublished(proposal) {
  return !!(proposal?.publicUrl || proposal?.publicToken);
}

/**
 * Signed = a DocuSign envelope for this proposal has completed.
 *
 * Read from contractInstances, which is where DocuSign state actually lives —
 * there is no proposal.docusign field, and no webhook. The status is refreshed
 * when the Contracts panel polls, so this is as fresh as the last poll and no
 * fresher. Deliberately not presented as live.
 */
export function isSigned(proposal, contractInstances = []) {
  return contractsForProposal(contractInstances, proposal?.id)
    .some((c) => c.status === "signed");
}

/**
 * Badges for one proposal.
 *
 * @param proposal          the record
 * @param activity          GET /view-activity payload, or null when not loaded
 * @param contractInstances state-blob contract instances
 */
export function webViewBadgesOf(proposal, activity = null, contractInstances = []) {
  const badges = [];
  if (!proposal) return badges;

  const views = activity?.published ? (activity.views || []) : [];
  const sends = activity?.published ? (activity.sends || []) : [];
  const opens = views.length;

  // Sent is superseded by Opened rather than shown alongside it — "Link sent"
  // next to "Opened ×3" is one badge too many for a toolbar that already
  // carries a status chip and possibly a LEAD chip.
  if (isPublished(proposal) && opens === 0) {
    badges.push({ ...BADGE_STYLE.sent, key: "sent" });
  }

  if (opens > 0) {
    const last = views[views.length - 1];
    /* With named recipients, "2 of 3 opened" answers the question Craig
       actually has — is this landing with the people who matter — where
       "Opened ×5" cannot tell one keen reader from three. Falls back to the raw
       count when there are no named recipients to count against, which is the
       case for a link that was only ever copied. */
    const named = sends.filter((s) => s.id);
    const openedNamed = named.filter((s) => s.opens > 0).length;
    let label;
    if (named.length > 1) {
      label = `${openedNamed} of ${named.length} opened`;
    } else if (named.length === 1 && named[0].opens > 0) {
      const who = named[0].recipientName || named[0].recipientEmail;
      label = who ? `Opened by ${who.split(" ")[0]}` : "Opened";
    } else {
      label = opens === 1 ? "Opened" : `Opened ×${opens}`;
    }
    badges.push({
      ...BADGE_STYLE.opened,
      key: "opened",
      label,
      title: last?.openedAt
        ? `${opens} open${opens === 1 ? "" : "s"} · last ${new Date(last.openedAt).toLocaleString("en-AU")}`
        : "",
    });
  }

  // Unanswered questions are the one thing here that needs Craig to DO
  // something, so they get a badge of their own rather than living only inside
  // the panel.
  const unanswered = activity?.published
    ? (activity.qa || []).filter((q) => !q.answer).length
    : 0;
  if (unanswered > 0) {
    badges.push({
      ...BADGE_STYLE.asked,
      key: "asked",
      label: unanswered === 1 ? "1 question" : `${unanswered} questions`,
      title: "Awaiting your answer",
    });
  }

  if (isSigned(proposal, contractInstances)) {
    badges.push({ ...BADGE_STYLE.signed, key: "signed" });
  }

  return badges;
}

/**
 * The dashboard's cheaper subset — only what is already in memory. No opens, no
 * questions, because both would cost a request per card.
 */
export function dashboardBadgesOf(proposal, contractInstances = []) {
  const badges = [];
  if (!proposal) return badges;
  if (isPublished(proposal)) badges.push({ ...BADGE_STYLE.sent, key: "sent" });
  if (isSigned(proposal, contractInstances)) badges.push({ ...BADGE_STYLE.signed, key: "signed" });
  return badges;
}

/**
 * Should the SPA reconcile proposal.status to "won"?
 *
 * Acceptance is recorded in the view blob by a public route, which deliberately
 * cannot write to the state blob. The SPA is the only writer of `status`, so it
 * closes the loop when it next loads activity.
 *
 * Returns true ONLY on the transition, so this never fights a human who has
 * since moved the proposal to "lost" — that would be the app arguing with the
 * person using it. Once status has moved off "won", it stays where they put it.
 */
export function needsWonReconciliation(proposal, activity) {
  if (!proposal || !activity?.published) return false;
  if (!activity.acceptance?.accepted) return false;
  return proposal.status !== "won" && !proposal.acceptanceReconciledAt;
}


/**
 * Should the SPA move proposal.status to reflect the approver's decision?
 *
 * Same shape as needsWonReconciliation and for the same reason: the decision is
 * recorded in the view blob by a public route, which deliberately cannot write
 * to the state blob, so the SPA closes the loop.
 *
 * ⚠ KEYED ON THE DECISION'S TIMESTAMP, not a boolean "done" flag. Approval is a
 * CYCLE — request, changes_needed, edit, request again, approved — so a single
 * "already reconciled" stamp would apply the first decision and then ignore
 * every one after it. Storing which decidedAt was applied means a NEW decision
 * reconciles and a re-read of the same one does not, which also stops it
 * fighting Craig if he moves the status by hand afterwards.
 *
 * Returns the status to write, or null.
 */
export function approvalStatusFor(proposal, activity) {
  if (!proposal || !activity?.published) return null;
  const decidedAt = activity.approval?.decidedAt || null;
  const state = activity.approval?.state || null;
  if (!decidedAt || !state) return null;
  if (proposal.approvalReconciledAt === decidedAt) return null;   // already applied
  if (state !== "approved" && state !== "changes_needed") return null;
  if (proposal.status === state) return null;
  return state;
}

/* Whether this proposal has been sent for internal approval at all — used to
   label the button "Send for approval" vs "Request approval again". */
export function hasApprovalSend(activity) {
  return !!(activity?.published && (activity.sends || []).some((s) => s.kind === "internal_approval"));
}
